
import React, { useState } from 'react';
import { UserProfile, PersonalityType, Language, AIEngine } from '../types';
import { PERSONALITY_CONFIG } from '../constants';
import { TRANSLATIONS } from '../translations';
import { 
  Activity, Brain, Users, Briefcase, DollarSign, Edit2, Save, X, Globe,
  User as UserIcon, Sparkles, ChevronLeft, CheckCircle2, Tag, Plus, Cpu
} from 'lucide-react';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

const ProfileView: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showPersonalityPicker, setShowPersonalityPicker] = useState(false);
  const [editedUser, setEditedUser] = useState<UserProfile>(user);

  const t = TRANSLATIONS[user.language];
  const currentPersona = PERSONALITY_CONFIG[user.selectedPersonality];

  const handleSave = () => {
    onUpdateUser(editedUser);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedUser(user);
    setIsEditing(false);
  };

  const changePersonality = (type: PersonalityType) => {
    setEditedUser(prev => ({
      ...prev,
      selectedPersonality: type
    }));
    setShowPersonalityPicker(false);
  };

  const changeLanguage = (lang: Language) => {
    setEditedUser(prev => ({
      ...prev,
      language: lang
    }));
  };

  const changeEngine = (engine: AIEngine) => {
    setEditedUser(prev => ({
      ...prev,
      preferredEngine: engine
    }));
  };

  const SectionHeader = ({ icon: Icon, title, colorClass }: any) => (
    <div className="flex items-center gap-3 mb-6 pb-2 border-b border-slate-100">
      <div className={`p-2 rounded-lg ${colorClass} bg-opacity-10`}>
        <Icon className={`w-5 h-5 ${colorClass}`} />
      </div>
      <h3 className="font-black text-slate-800">{title}</h3>
    </div>
  );

  return (
    <div className="space-y-6 pb-24 animate-fade-in">
      
      {/* Personality Change Modal */}
      {showPersonalityPicker && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-black text-slate-800">{t.select_companion}</h3>
              <button onClick={() => setShowPersonalityPicker(false)} className="p-2 bg-slate-100 rounded-full">
                <X className="w-6 h-6 text-slate-500" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {Object.values(PERSONALITY_CONFIG).map((persona: any) => (
                <button
                  key={persona.id}
                  onClick={() => changePersonality(persona.id)}
                  className={`p-4 rounded-2xl border-2 transition-all flex items-start gap-3 text-right
                    ${editedUser.selectedPersonality === persona.id 
                      ? `${persona.border} ${persona.bg} shadow-md` 
                      : 'border-slate-100 hover:border-slate-200 bg-slate-50'
                    }`}
                >
                  <div className={`p-2 rounded-xl bg-white shadow-sm ${persona.color}`}>
                    {persona.icon}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-black text-slate-800">{t[persona.labelKey]}</h4>
                    <p className="text-[10px] text-slate-500 font-bold line-clamp-2">{t[persona.detailsKey]}</p>
                  </div>
                  {editedUser.selectedPersonality === persona.id && <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />}
                </button>
              ))}
            </div>
            <button onClick={() => setShowPersonalityPicker(false)} className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black">{t.cancel}</button>
          </div>
        </div>
      )}

      {/* Profile Header */}
      <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
        <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
          <div className="w-24 h-24 rounded-full bg-slate-100 border-4 border-white shadow-xl overflow-hidden">
               <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${editedUser.name}`} alt="Profile" className="w-full h-full object-cover" />
          </div>
          <div className="text-center md:text-right">
            {isEditing ? (
              <input 
                value={editedUser.name}
                onChange={(e) => setEditedUser({...editedUser, name: e.target.value})}
                className="text-2xl font-black text-slate-800 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 w-full focus:outline-none text-center md:text-right"
              />
            ) : (
              <>
                <h2 className="text-3xl font-black text-slate-800 tracking-tight">{user.name}</h2>
                <div className="flex flex-wrap gap-2 mt-2 justify-center md:justify-start">
                  <span className="text-xs bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-black">
                    {t.level} {user.level}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
        
        <div className="flex gap-2 w-full md:w-auto relative z-10">
          {isEditing ? (
            <>
              <button onClick={handleCancel} className="flex-1 md:flex-none px-6 py-3.5 rounded-2xl border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 transition-all font-black flex items-center justify-center gap-2">
                <X className="w-4 h-4" /> {t.cancel}
              </button>
              <button onClick={handleSave} className="flex-1 md:flex-none px-10 py-3.5 rounded-2xl bg-indigo-600 text-white hover:bg-indigo-700 transition-all shadow-lg font-black flex items-center justify-center gap-2">
                <Save className="w-4 h-4" /> {t.save}
              </button>
            </>
          ) : (
            <button onClick={() => setIsEditing(true)} className="flex-1 md:flex-none px-10 py-3.5 rounded-2xl bg-slate-900 text-white hover:bg-slate-800 transition-all shadow-xl font-black flex items-center justify-center gap-2">
              <Edit2 className="w-4 h-4" /> {t.edit_profile}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        
        {/* Language Selection */}
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
          <SectionHeader icon={Globe} title={t.language} colorClass="text-blue-500" />
          <div className="flex gap-4">
            <button 
              onClick={() => isEditing && changeLanguage(Language.KURDISH)}
              disabled={!isEditing}
              className={`flex-1 py-4 rounded-2xl font-black border-2 transition-all flex items-center justify-center gap-2 ${editedUser.language === Language.KURDISH ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-slate-50 border-slate-100 text-slate-500'}`}
            >
              {t.kurdish}
            </button>
            <button 
              onClick={() => isEditing && changeLanguage(Language.PERSIAN)}
              disabled={!isEditing}
              className={`flex-1 py-4 rounded-2xl font-black border-2 transition-all flex items-center justify-center gap-2 ${editedUser.language === Language.PERSIAN ? 'bg-indigo-50 border-indigo-500 text-indigo-700' : 'bg-slate-50 border-slate-100 text-slate-500'}`}
            >
              {t.persian}
            </button>
          </div>
        </div>

        {/* AI Engine Selection */}
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
          <SectionHeader icon={Cpu} title={t.select_ai_engine} colorClass="text-purple-500" />
          <div className="grid grid-cols-1 gap-3">
            {[
              { id: AIEngine.GEMINI, label: t.gemini_label },
              { id: AIEngine.DEEPSEEK, label: t.deepseek_label },
              { id: AIEngine.GROK, label: t.grok_label }
            ].map(engine => (
              <button 
                key={engine.id}
                onClick={() => isEditing && changeEngine(engine.id)}
                disabled={!isEditing}
                className={`p-4 rounded-2xl font-black border-2 transition-all flex items-center justify-between ${editedUser.preferredEngine === engine.id ? 'bg-purple-50 border-purple-500 text-purple-700' : 'bg-slate-50 border-slate-100 text-slate-500'}`}
              >
                <span>{engine.label}</span>
                {editedUser.preferredEngine === engine.id && <CheckCircle2 className="w-5 h-5 text-purple-500" />}
              </button>
            ))}
          </div>
        </div>

        {/* Personality Summary */}
        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100 md:col-span-2">
          <SectionHeader icon={Sparkles} title={t.personality_title} colorClass="text-amber-500" />
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className={`p-6 rounded-3xl ${currentPersona.bg} ${currentPersona.color} shadow-inner`}>
              {currentPersona.icon}
            </div>
            <div className="flex-1 text-center md:text-right">
              <h4 className="text-xl font-black text-slate-800 mb-2">{t[currentPersona.labelKey]}</h4>
              <p className="text-slate-500 text-sm font-bold leading-relaxed mb-6">{t[currentPersona.detailsKey]}</p>
              {isEditing && (
                  <button 
                    onClick={() => setShowPersonalityPicker(true)}
                    className="inline-flex items-center gap-2 text-indigo-600 font-black text-sm hover:underline"
                  >
                    {t.select_companion} <ChevronLeft className="w-4 h-4 rtl:rotate-180" />
                  </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileView;
